import {
  Flex,
  Icon,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { formatToCurrency } from '@common/utils/formatter';
import { ClockIcon, HouseIcon, MapIcon } from 'lucide-react';
import { SignalIcon, SignalStrength } from '~/common/icons/SignalIcon';
import { formatDistance } from './common.utils';

interface SourceTagsProps {
  sourceLabel?: string;
  confidence?: SignalStrength;
  distanceMiles?: number;
  timePeriodMonths?: number;
  isExpanded?: boolean;
  zipCode?: string;
  priceRange?: {
    min?: number;
    max?: number;
  };
  subType?: string;
}

export const SourceTags = ({
  sourceLabel,
  confidence,
  distanceMiles,
  timePeriodMonths,
  isExpanded,
  zipCode,
  subType,
}: SourceTagsProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Tooltip
      isOpen={isOpen}
      label={
        <SourceTagsTooltipContent
          confidence={confidence}
          distanceMiles={distanceMiles}
          sourceLabel={sourceLabel}
          subType={subType}
          timePeriodMonths={timePeriodMonths}
          zipCode={zipCode}
        />
      }
      variant="muted"
    >
      <Flex
        alignItems="center"
        data-testid="source-tags-tooltip-trigger"
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        gap={1}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      >
        {!!sourceLabel && (
          <Tag
            borderRadius="3px"
            color="whiteAlpha.600"
            size="sm"
            textTransform={sourceLabel === 'mls' ? 'uppercase' : 'capitalize'}
          >
            {sourceLabel}
          </Tag>
        )}
        {!!confidence && (
          <Tag borderRadius="3px" px={1} size="sm" width={4}>
            <SignalIcon
              color="purple.500"
              strength={confidence as unknown as SignalStrength}
            />
          </Tag>
        )}
        {(!!distanceMiles || !!zipCode) && isExpanded && (
          <>
            {!!zipCode && (
              <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
                <Flex alignItems="center" gap={1}>
                  <Icon as={MapIcon} />
                  <Text>{zipCode}</Text>
                </Flex>
              </Tag>
            )}
            {!!distanceMiles && (
              <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
                <Flex alignItems="center" gap={1}>
                  {!zipCode && <Icon as={MapIcon} />}
                  <Text>{formatDistance(distanceMiles)}</Text>
                </Flex>
              </Tag>
            )}
          </>
        )}
        {!!timePeriodMonths && isExpanded && (
          <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
            <Flex alignItems="center" gap={1}>
              <Icon as={ClockIcon} />
              {timePeriodMonths} months
            </Flex>
          </Tag>
        )}
      </Flex>
    </Tooltip>
  );
};

export const SourceTagsTooltipContent = ({
  sourceLabel,
  confidence,
  distanceMiles,
  timePeriodMonths,
  zipCode,
  priceRange,
  subType,
}: SourceTagsProps) => {
  return (
    <Flex direction="column" gap={2} width="210px">
      {!!sourceLabel && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Data source</Text>
          <Tag
            borderRadius="3px"
            color="whiteAlpha.600"
            size="sm"
            textTransform={sourceLabel === 'mls' ? 'uppercase' : 'capitalize'}
          >
            {sourceLabel}
          </Tag>
        </Flex>
      )}
      {!!confidence && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Confidence</Text>
          <Tag borderRadius="3px" gap={1} px={1} size="sm">
            <SignalIcon
              color="purple.500"
              strength={confidence as unknown as SignalStrength}
            />
            <Text color="whiteAlpha.600" textTransform="capitalize">
              {confidence.toLowerCase()}
            </Text>
          </Tag>
        </Flex>
      )}
      {(!!distanceMiles || !!zipCode) && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Distance</Text>
          <Flex alignItems="center" gap={1}>
            {!!zipCode && (
              <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
                <Flex alignItems="center" gap={1}>
                  <Icon as={MapIcon} />
                  <Text>{zipCode}</Text>
                </Flex>
              </Tag>
            )}
            {!!distanceMiles && (
              <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
                <Flex alignItems="center" gap={1}>
                  {!zipCode && <Icon as={MapIcon} />}
                  <Text>{formatDistance(distanceMiles)}</Text>
                </Flex>
              </Tag>
            )}
          </Flex>
        </Flex>
      )}
      {!!timePeriodMonths && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Time period</Text>
          <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
            <Flex alignItems="center" gap={1}>
              <Icon as={ClockIcon} />
              {timePeriodMonths} months
            </Flex>
          </Tag>
        </Flex>
      )}
      {!!priceRange && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Price range</Text>
          <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
            {formatToCurrency(priceRange.min, 2, 'compact')} to{' '}
            {formatToCurrency(priceRange.max, 2, 'compact')}
          </Tag>
        </Flex>
      )}
      {!!subType && (
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          <Text>Subtype</Text>
          <Tag borderRadius="3px" color="whiteAlpha.600" size="sm">
            <Flex alignItems="center" gap={1}>
              <Icon as={HouseIcon} />
              <Text>{subType}</Text>
            </Flex>
          </Tag>
        </Flex>
      )}
    </Flex>
  );
};
